import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

type SectionSourceDraft = "headings" | { linePrefix: string; suffix?: string; title?: string };

interface SectionsDraft {
  from?: SectionSourceDraft[];
}

const HEADINGS = "headings";
const MARKER = "marker";

/**
 * `kind: "sectionsFrom"` — pack `Manifest.sections` (docs/231-SECTION-SPLITTER-
 * MANIFEST-FIELD.md): `{from: SectionSource[]}`, each source either the literal
 * `"headings"` or a `{linePrefix,suffix?,title?}` marker — first-match-per-line,
 * order matters. An empty `from[]` collapses `sections` itself to `undefined`.
 */
export function SectionsFromEditor({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const draft: SectionsDraft = value && typeof value === "object" ? (value as SectionsDraft) : {};
  const rows: SectionSourceDraft[] = Array.isArray(draft.from) ? draft.from : [];

  const commit = (next: SectionSourceDraft[]) => onChange(next.length > 0 ? { from: next } : undefined);
  const update = (i: number, next: SectionSourceDraft) => commit(rows.map((r, idx) => (idx === i ? next : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const addHeadings = () => commit([...rows, HEADINGS]);
  const addMarker = () => commit([...rows, { linePrefix: "" }]);

  const modeOf = (r: SectionSourceDraft) => (r === HEADINGS ? HEADINGS : MARKER);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
        {help ? ` — ${help}` : ""}
      </Typography>
      <Stack spacing={1}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
            <TextField
              select
              size="small"
              label="Source"
              value={modeOf(row)}
              onChange={(e) =>
                update(i, e.target.value === HEADINGS ? HEADINGS : { linePrefix: "" })
              }
              sx={{ minWidth: 130 }}
            >
              <MenuItem value={HEADINGS}># headings</MenuItem>
              <MenuItem value={MARKER}>line-prefix marker</MenuItem>
            </TextField>
            {modeOf(row) === MARKER && typeof row === "object" ? (
              <>
                <TextField
                  size="small"
                  label="Line prefix"
                  value={row.linePrefix}
                  onChange={(e) => update(i, { ...row, linePrefix: e.target.value })}
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  size="small"
                  label="Suffix (optional)"
                  value={row.suffix ?? ""}
                  onChange={(e) => update(i, { ...row, suffix: e.target.value || undefined })}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  size="small"
                  label="Title label (optional)"
                  value={row.title ?? ""}
                  onChange={(e) => update(i, { ...row, title: e.target.value || undefined })}
                  sx={{ minWidth: 140 }}
                />
              </>
            ) : null}
            <Button size="small" color="error" onClick={() => remove(i)}>
              Remove
            </Button>
          </Box>
        ))}
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={addHeadings}>
            + Add "# headings"
          </Button>
          <Button size="small" variant="outlined" onClick={addMarker}>
            + Add line-prefix marker
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
